import { useState, useEffect, useRef, useCallback } from 'react';
import { usersAPI } from '../lib/api';
import { Textarea } from './ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Loader2 } from 'lucide-react';

/**
 * MentionTextarea — Textarea that supports @-mentions.
 *
 * When the user types `@` followed by a name fragment, an autocomplete dropdown
 * shows alumni members matching that fragment. Selecting one inserts
 * `@[Display Name](user_id)` into the text. The output (via onChange) is that
 * markup string — backend stores it as-is, and <MentionText> renders mentions
 * as clickable profile links.
 *
 * Drop-in replacement for `<Textarea>` — accepts value/onChange/placeholder/rows/className.
 */
export default function MentionTextarea({ value, onChange, placeholder, rows = 3, className = '', onKeyDown, ...rest }) {
  const textareaRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  // Caret-anchored mention regex: `@` + non-whitespace chars at end of caret position.
  // Matches `@Marcus`, `@Sam.K`, etc. Stops at the next whitespace.
  const MENTION_AT_CARET = /(^|\s)@([^\s@]{0,40})$/;

  const detectQuery = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    const caret = ta.selectionStart || 0;
    const before = (value || '').substring(0, caret);
    const m = before.match(MENTION_AT_CARET);
    if (m && m[2] !== undefined) {
      setQuery(m[2]);
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
      setQuery('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Re-evaluate dropdown whenever value or caret moves
  useEffect(() => { detectQuery(); }, [value, detectQuery]);

  // Debounced search
  useEffect(() => {
    if (!showDropdown) {
      setResults([]);
      setLoading(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      // Empty query right after @: show a few recent/popular members
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await usersAPI.listUsers({ search: query.trim(), limit: 6 });
        setResults(Array.isArray(res.data) ? res.data : []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, showDropdown]);

  // Close dropdown on outside click
  useEffect(() => {
    const onMouseDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  const insertMention = (user) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const caret = ta.selectionStart || 0;
    const before = (value || '').substring(0, caret);
    const after = (value || '').substring(caret);
    // Replace the trailing @query with the structured mention + trailing space
    const newBefore = before.replace(MENTION_AT_CARET, (full, lead) => {
      return `${lead || ''}@[${user.name}](${user.user_id}) `;
    });
    const newValue = newBefore + after;
    onChange(newValue);
    setShowDropdown(false);
    // Move caret to end of inserted mention on next tick
    setTimeout(() => {
      const newCaret = newBefore.length;
      ta.focus();
      ta.setSelectionRange(newCaret, newCaret);
    }, 0);
  };

  return (
    <div ref={containerRef} className="relative">
      <Textarea
        ref={textareaRef}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        onSelect={detectQuery}
        onKeyUp={detectQuery}
        onClick={detectQuery}
        onKeyDown={(e) => {
          // When the @-dropdown is showing, Enter selects the first match
          // (don't submit / don't add newline) and Escape closes the dropdown.
          if (showDropdown && results.length > 0 && e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            insertMention(results[0]);
            return;
          }
          if (showDropdown && e.key === 'Escape') {
            e.preventDefault();
            setShowDropdown(false);
            return;
          }
          onKeyDown?.(e);
        }}
        placeholder={placeholder}
        rows={rows}
        className={className}
        {...rest}
      />
      {showDropdown && (query.trim() || loading) && (
        <div className="absolute z-50 mt-1 left-0 right-0 bg-white border border-slate-200 rounded-xl shadow-lg max-h-56 overflow-y-auto">
          {loading && results.length === 0 && (
            <div className="flex items-center gap-2 px-3 py-2.5 text-sm text-slate-gray">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />Searching alumni...
            </div>
          )}
          {!loading && results.length === 0 && query.trim() && (
            <p className="px-3 py-2.5 text-sm text-slate-gray">No alumni match "{query}".</p>
          )}
          {results.map(u => (
            <button
              key={u.user_id}
              type="button"
              onMouseDown={(e) => e.preventDefault()} // keep textarea focus
              onClick={() => insertMention(u)}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 transition-colors text-left"
            >
              <Avatar className="w-7 h-7 flex-shrink-0">
                <AvatarImage src={u.profile_photo} />
                <AvatarFallback className="bg-mint-fresh text-dark-slate text-[10px]">{u.name?.charAt(0)}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-dark-slate truncate">{u.name}</p>
                {(u.title || u.institution) && (
                  <p className="text-[11px] text-slate-gray truncate">
                    {[u.title, u.institution].filter(Boolean).join(' · ')}
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
